import {
  Avatar,
  Box,
  Grid2,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Switch,
  SwitchProps,
  TextField,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import CircleWarningIcon from "assets/svg/CircleWarningIcon";
import PencilIcon from "assets/svg/PencilIcon";
import UserProfileIcon from "assets/svg/UserProfileIcon";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import * as motion from "motion/react-client";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reduxStore/store";
import theme, { responsiveFontSize } from "themes/Theme";
import TwoFAEnabler from "./TwoFAEnabler";
import EnableTwoFA from "components/EnableTwoFA/EnableTwoFA";
import { FETCH_PROFILE_TYPE } from "reduxStore/actions/authActionType";
import DisableTwoFA from "components/DisableTwoFA/DisableTwoFA";
import CloseIcon from "assets/svg/CloseIcon";
import { updateUserPassword } from "services/apiService";
import { UpdatePasswordRequest } from "reduxStore/dataTypes";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";

const SecuritySettings = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [editable, setEditable] = useState(false);
  const [oldPassword, setOldPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);
  const [oldPasswordError, setOldPasswordError] = useState<string | null>(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [isOldPasswordVisible, setIsOldPasswordVisible] =
    useState<boolean>(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] =
    useState<boolean>(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState<boolean>(false);
  const [showTFAModal, setShowTFAModal] = useState<boolean>(false);
  const [enableTwoFAModal, setEnableTwoFAModal] = useState<boolean>(false);
  const [disableTwoFAModal, setDisableTwoFAModal] = useState<boolean>(false);
  const { user, loading } = useSelector((state: RootState) => state.user);
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const toggleEditable = () => {
    setEditable(!editable);
  };

  useEffect(() => {
    dispatch({
      type: FETCH_PROFILE_TYPE,
    });
  }, []);

  const IOSSwitch = styled((props: SwitchProps) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#65C466",
          opacity: 1,
          border: 0,
          ...theme.applyStyles("dark", {
            backgroundColor: "#2ECA45",
          }),
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.grey[100],
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[600],
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.7,
        ...theme.applyStyles("dark", {
          opacity: 0.3,
        }),
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 22,
      height: 22,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
      ...theme.applyStyles("dark", {
        backgroundColor: "#39393D",
      }),
    },
  }));

  const handleTwoFACheckedChange = () => {
    if (user?.data?.personal_details?.twoFactorEnabled === 1) {
      setDisableTwoFAModal(true);
    } else {
      setShowTFAModal(true);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (
        oldPassword === "" ||
        oldPassword === null ||
        oldPassword === undefined
      ) {
        setOldPasswordError("Old Password is required");
      } else if (
        newPassword === "" ||
        newPassword === null ||
        newPassword === undefined
      ) {
        setNewPasswordError("New Password is required");
      } else if (
        confirmPassword === "" ||
        confirmPassword === null ||
        confirmPassword === undefined
      ) {
        setConfirmPasswordError("Confirm Password is required");
      } else if (newPassword !== confirmPassword) {
        setConfirmPasswordError(
          "New Password and Confirm Password do not match"
        );
      } else if (!passwordRegex.test(newPassword)) {
        setNewPasswordError("Password validation failed.");
      } else {
        const body: UpdatePasswordRequest = {
          currentPassword: oldPassword || "",
          newPassword: newPassword || "",
        };

        const response = await updateUserPassword(body);
        if (response && response.status === 200) {
          toggleEditable();
          toast.success(response?.data?.message);
          setOldPassword("");
          setNewPassword("");
          setConfirmPassword("");
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 30,
      }}
    >
      <Box sx={{ ...styles.container, p: isMobile ? 1.5 : 3 }}>
        <Box sx={styles.flexSpaceBetween(isMobile)}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            gap={1}
          >
            <Avatar
              sx={{
                width: isMobile ? 48 : 60,
                height: isMobile ? 48 : 60,
                borderRadius: "50%", // Ensure it's a circle
                bgcolor: "#fffede",
                border: "1px solid #ccc", // Added circle border
              }}
            >
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: isMobile ? 20 : 24,
                  textTransform: "uppercase",
                }}
              >
                {user?.data?.personal_details?.firstName?.charAt(0)}
              </Typography>
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: isMobile ? 20 : 24,
                  textTransform: "uppercase",
                }}
              >
                {user?.data?.personal_details?.lastName?.charAt(0)}
              </Typography>
            </Avatar>
            <Box>
              <Typography sx={styles.profileName}>{`${
                user?.data?.personal_details?.firstName || ""
              } ${user?.data?.personal_details?.lastName || ""}`}</Typography>
              <Typography sx={styles.profileEmail}>
                {`${user?.data?.personal_details?.email || ""}`}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ width: isMobile ? "100%" : "auto", mt: isMobile ? 2 : 0 }}>
            <PrimaryButton
              title={editable ? "Cancel" : "Edit"}
              onClick={toggleEditable}
              rightIcon={editable ? <CloseIcon color="#fff" /> : <PencilIcon />}
              viewStyle={{
                borderRadius: "999px",
                padding: "10px 24px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                color: "#fff",
                fontWeight: 600,
                fontSize: responsiveFontSize(14),
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.2)",
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                },
              }}
            />
          </Box>
        </Box>
        {editable ? (
          <>
            <Grid2 container spacing={isMobile ? 1 : 2} mt={2}>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Old Password
                </Typography>
                <TextField
                  type={isOldPasswordVisible ? "text" : "password"}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter old password"
                  value={oldPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.value) {
                      setOldPassword(e.target.value);
                    } else {
                      setOldPassword(null);
                    }
                  }}
                  slotProps={{
                    input: {
                      sx: {
                        fontSize: "0.875rem",
                        color: theme.palette.text.primary,
                        fontWeight: 400,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setIsOldPasswordVisible(!isOldPasswordVisible)
                            }
                            aria-label="toggle password visibility"
                          >
                            {isOldPasswordVisible ? (
                              <EyeHiddenIcon />
                            ) : (
                              <EyeVisibleIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  slots={{ input: OutlinedInput }}
                  error={oldPasswordError === null ? false : true}
                  helperText={oldPasswordError || "" || undefined}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  New Password
                </Typography>
                <TextField
                  type={isNewPasswordVisible ? "text" : "password"}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.value) {
                      setNewPassword(e.target.value);
                    } else {
                      setNewPassword(null);
                    }
                  }}
                  slotProps={{
                    input: {
                      sx: {
                        fontSize: "0.875rem",
                        color: theme.palette.text.primary,
                        fontWeight: 400,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setIsNewPasswordVisible(!isNewPasswordVisible)
                            }
                            aria-label="toggle password visibility"
                          >
                            {isNewPasswordVisible ? (
                              <EyeHiddenIcon />
                            ) : (
                              <EyeVisibleIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  slots={{ input: OutlinedInput }}
                  error={newPasswordError === null ? false : true}
                  helperText={newPasswordError || "" || undefined}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Confirm Password
                </Typography>
                <TextField
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter confirm password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.value) {
                      setConfirmPassword(e.target.value);
                    } else {
                      setConfirmPassword(null);
                    }
                  }}
                  slotProps={{
                    input: {
                      sx: {
                        fontSize: "0.875rem",
                        color: theme.palette.text.primary,
                        fontWeight: 400,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setIsConfirmPasswordVisible(
                                !isConfirmPasswordVisible
                              )
                            }
                            aria-label="toggle password visibility"
                          >
                            {isConfirmPasswordVisible ? (
                              <EyeHiddenIcon />
                            ) : (
                              <EyeVisibleIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  slots={{ input: OutlinedInput }}
                  error={confirmPasswordError === null ? false : true}
                  helperText={confirmPasswordError || "" || undefined}
                />
              </Grid2>
            </Grid2>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              mt={2}
              mb={1}
              sx={{
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
              }}
            >
              <CircleWarningIcon
                width={isMobile ? 20 : 24}
                height={isMobile ? 20 : 24}
              />
              <Typography sx={styles.passwordHintInfo}>
                Make sure you password contain at least 8 characters, 1
                uppercase letter, 1 lowercase letter, 1 number and 1 special
                character
              </Typography>
            </Stack>
            <Box sx={{ width: isMobile ? "100%" : "auto" }}>
              <PrimaryButton
                title="Update Password"
                onClick={handlePasswordChange}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography sx={styles.twoFATitle}>Enable | Disable 2FA</Typography>
            <IOSSwitch
              sx={{ m: 1 }}
              checked={
                user?.data?.personal_details?.twoFactorEnabled === 1
                  ? true
                  : false
              }
              onChange={handleTwoFACheckedChange}
            />
          </>
        )}

        {showTFAModal && (
          <TwoFAEnabler
            open={showTFAModal}
            handleClose={() => setShowTFAModal(false)}
            onSuccess={() => {
              setShowTFAModal(false);
              setEnableTwoFAModal(true);
            }}
          />
        )}

        {enableTwoFAModal && (
          <EnableTwoFA
            open={enableTwoFAModal}
            handleClose={() => setEnableTwoFAModal(false)}
            onSuccess={() => {
              setEnableTwoFAModal(false);
            }}
          />
        )}
        {disableTwoFAModal && (
          <DisableTwoFA
            open={disableTwoFAModal}
            handleClose={() => setDisableTwoFAModal(false)}
            onSuccess={() => {
              setDisableTwoFAModal(false);
            }}
          />
        )}
      </Box>
    </motion.div>
  );
};
export default SecuritySettings;
const styles = {
  container: {
    backgroundColor: "#FFFFFF",
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
    borderRadius: "1em",
    marginTop: "2em",
    p: 3, // will be overridden by inline p in Box
  },
  flexSpaceBetween: (isMobile: boolean) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: isMobile ? 2 : 0,
  }),
  profileName: {
    fontSize: responsiveFontSize(20),
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  profileEmail: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  passwordHintInfo: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  twoFATitle: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.primary.main,
    fontWeight: 400,
    marginTop: "1em",
  },
  avatarImage: {
    width: 60,
    height: 60,
    aspectRatio: "1/1",
    objectFit: "cover",
  },
};
